import {
    faChevronDown,
    faChevronRight,
    faCompressArrowsAlt,
    faExpandArrowsAlt,
    faFolder,
    faFolderOpen,
    faSearch
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useMemo, useState } from "react";
import { Badge, Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { TextFieldAtom } from "../../../../components/atoms/atom";
import DropdownAtom from "../../../../components/atoms/dropdown/dropdown";
import {
    selectFormState,
    selectInvoicePositions,
    selectLVData,
    setInvoicePositionQuantity,
} from "../../../../store/slices/invoiceV2Slice";
import { formatCurrencyToLocale } from "../../../../utils/method";
import ExpandablePositionsTable from "./ExpandablePositionsTable";
import PositionTypeLegend from "./PositionTypeLegend";
import RecursiveGroupRenderer from "./RecursiveGroupRenderer";
import { calculateTotals, getAllGroupIds } from "./hierarchicalHelpers";
import "./HierarchicalPositions.scss";

const HierarchicalPositions = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const lvData = useSelector(selectLVData);
    const formState = useSelector(selectFormState);
    const invoicePositions = useSelector(selectInvoicePositions);
    const lvs = useMemo(() => lvData?.lvs || [], [lvData?.lvs]);
    const isReadonly = formState?.readonly || false;

    const [searchTerm, setSearchTerm] = useState("");
    const [positionFilter, setPositionFilter] = useState("all");
    const [expandedLVs, setExpandedLVs] = useState(new Set());
    const [expandedGroups, setExpandedGroups] = useState(new Set());

    const positionFilterOptions = [
        { value: "all", label: t('hierarchical-positions.all-positions') },
        { value: "current", label: t('hierarchical-positions.current-invoice-positions') },
        { value: "new", label: t('hierarchical-positions.new-positions') },
        { value: "carried-forward", label: t('hierarchical-positions.carried-forward-positions') },
        { value: "modified-carried-forward", label: t('hierarchical-positions.modified-carried-forward-positions') }
    ];

    const handlePositionQuantityChange = useCallback((position, newQuantity) => {
        dispatch(setInvoicePositionQuantity({
            positionId: position.id,
            quantity: parseFloat(newQuantity) || 0,
            position,
        }));
    }, [dispatch]);

    const getPositionType = useCallback((element) => {
        const invoicePosition = invoicePositions.get(element.id);
        if (!invoicePosition) return 'empty';

        const fromPreviousInvoice = invoicePosition.fromPreviousInvoice || false;
        const originalQuantity = parseFloat(invoicePosition.lastAllocation?.invoiceQuantity || 0);
        const currentQuantity = parseFloat(invoicePosition.invoice?.quantity || 0);
        const aufmassQuantity = parseFloat(invoicePosition.lastAllocation?.aufmassQuantity || 0);

        if (!fromPreviousInvoice) {
            return currentQuantity > 0 ? 'new' : 'empty';
        }
        if (aufmassQuantity > 0 && Math.abs(currentQuantity - aufmassQuantity) < 0.001) {
            return 'carried-forward';
        }
        return Math.abs(originalQuantity - currentQuantity) > 0.001
            ? 'modified-carried-forward'
            : 'carried-forward';
    }, [invoicePositions]);

    const mergeInvoiceDataIntoPosition = useCallback((lvPosition) => {
        const invoicePosition = invoicePositions.get(lvPosition.id);
        if (!invoicePosition) return lvPosition;
        return {
            ...lvPosition,
            unitPrice: invoicePosition.unitPrice || lvPosition.unitPrice,
            invoiceQuantity: invoicePosition.invoice?.quantity || 0,
            invoiceAmount: invoicePosition.invoice?.amount || 0,
            lv: {
                unitPrice: lvPosition.unitPrice,
                quantity: lvPosition.quantity,
                totalPrice: lvPosition.totalPrice,
            },
            invoice: invoicePosition.invoice,
            allocation: invoicePosition.allocation,
            fromPreviousInvoice: invoicePosition.fromPreviousInvoice || false,
        };
    }, [invoicePositions]);

    const mergeInvoiceDataRecursively = useCallback((elements) => {
        if (!elements) return elements;
        return elements.map(element => element.isGroup
            ? { ...element, elements: mergeInvoiceDataRecursively(element.elements) }
            : mergeInvoiceDataIntoPosition(element)
        );
    }, [mergeInvoiceDataIntoPosition]);

    const filterElementsBySearch = useCallback((elements, searchTerm, positionFilter) => {
        if (!elements) return elements;
        const term = searchTerm?.toLowerCase() || "";

        return elements.filter(element => {
            if (element.isGroup) {
                const groupMatches = !searchTerm || element.name?.toLowerCase().includes(term);
                const filteredChildren = element.elements
                    ? filterElementsBySearch(element.elements, searchTerm, positionFilter)
                    : [];
                if (groupMatches || filteredChildren.length > 0) {
                    return { ...element, elements: filteredChildren };
                }
                return false;
            } else {
                if (positionFilter === "current") {
                    const invoicePosition = invoicePositions.get(element.id);
                    if ((invoicePosition?.invoice?.quantity || 0) <= 0) return false;
                } else if (positionFilter !== "all") {
                    if (getPositionType(element) !== positionFilter) return false;
                }
                if (!searchTerm) return true;
                return (
                    element.name?.toLowerCase().includes(term) ||
                    element.itemNumber?.toString().toLowerCase().includes(term) ||
                    element.description?.toLowerCase().includes(term) ||
                    element.unit?.toLowerCase().includes(term)
                );
            }
        }).map(element => {
            if (element.isGroup && element.elements) {
                return { ...element, elements: filterElementsBySearch(element.elements, searchTerm, positionFilter) };
            }
            return element;
        }).filter(element => !element.isGroup || (element.elements && element.elements.length > 0));
    }, [invoicePositions, getPositionType]);

    const filteredLVs = useMemo(() => {
        const lvsWithInvoiceData = lvs.map(lv => ({
            ...lv,
            elements: mergeInvoiceDataRecursively(lv.elements)
        }));
        return lvsWithInvoiceData.map(lv => ({
            ...lv,
            elements: lv.elements
                ? filterElementsBySearch(lv.elements, searchTerm, positionFilter)
                : []
        })).filter(lv => {
            const nameMatches = !searchTerm || lv.name?.toLowerCase().includes(searchTerm.toLowerCase());
            const hasFilteredElements = lv.elements && lv.elements.length > 0;
            return nameMatches || hasFilteredElements;
        }).filter(lv => lv.elements && lv.elements.length > 0);
    }, [lvs, searchTerm, positionFilter, filterElementsBySearch, mergeInvoiceDataRecursively]);

    const totalAmount = filteredLVs.reduce(
        (sum, lv) => sum + (lv.totalPrice || lv.totalPriceGross || 0), 0
    );
    const lvTotals = filteredLVs.reduce((acc, lv) => {
        const totals = calculateTotals(lv.elements);
        return { groups: acc.groups + totals.groups, positions: acc.positions + totals.positions };
    }, { groups: 0, positions: 0 });

    const toggleLV = useCallback((lvId) => {
        setExpandedLVs(prev => {
            const next = new Set(prev);
            next.has(lvId) ? next.delete(lvId) : next.add(lvId);
            return next;
        });
    }, []);

    const toggleGroup = useCallback((groupId) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            next.has(groupId) ? next.delete(groupId) : next.add(groupId);
            return next;
        });
    }, []);

    const expandAll = useCallback(() => {
        const allLVs = new Set(filteredLVs.map(lv => lv.id));
        const allGroups = new Set();
        filteredLVs.forEach(lv => {
            getAllGroupIds(lv.elements).forEach(id => allGroups.add(id));
        });
        setExpandedLVs(allLVs);
        setExpandedGroups(allGroups);
    }, [filteredLVs]);

    const collapseAll = useCallback(() => {
        setExpandedLVs(new Set());
        setExpandedGroups(new Set());
    }, []);

    return (
        <div className="positions-panel">
            {/* Compact toolbar */}
            <div className="positions-toolbar d-flex align-items-center gap-2 flex-wrap px-3 py-2 hp-toolbar">
                <div className="hp-search">
                    <TextFieldAtom
                        type="text"
                        placeholder={t('hierarchical-positions.search-placeholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        showClearIcon={true}
                        onClear={() => setSearchTerm('')}
                        clearIconTitle={t('hierarchical-positions.clear-search')}
                        suffix={<FontAwesomeIcon icon={faSearch} />}
                        size="sm"
                    />
                </div>
                <div className="hp-filter">
                    <DropdownAtom
                        value={positionFilter}
                        options={positionFilterOptions}
                        onChange={(e) => setPositionFilter(e.target.value)}
                        size="small"
                        isClearable={false}
                        isSearchable={false}
                    />
                </div>
                <div className="d-flex gap-1 flex-shrink-0">
                    <Button variant="outline-secondary" size="sm" onClick={expandAll}
                        title={t('hierarchical-positions.expand-all')}>
                        <FontAwesomeIcon icon={faExpandArrowsAlt} />
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={collapseAll}
                        title={t('hierarchical-positions.collapse-all')}>
                        <FontAwesomeIcon icon={faCompressArrowsAlt} />
                    </Button>
                </div>
                <div className="ms-auto flex-shrink-0 d-none d-md-flex align-items-center gap-3">
                    <small className="text-muted">
                        {filteredLVs.length} {t('hierarchical-positions.lvs')} ·{' '}
                        {lvTotals.groups} {t('hierarchical-positions.groups')} ·{' '}
                        {lvTotals.positions} {t('hierarchical-positions.positions')}
                    </small>
                    <span className="fw-semibold hp-total">
                        {formatCurrencyToLocale(totalAmount)}
                    </span>
                </div>
            </div>

            <div className="positions-body">
                <div className="px-3 pb-3 pt-2">
                    {filteredLVs.length === 0 ? (
                        <div className="text-center py-5">
                            <FontAwesomeIcon icon={faSearch} size="3x" className="text-muted mb-3" />
                            <h5 className="text-muted">{t('hierarchical-positions.no-results-found')}</h5>
                            <p className="text-muted small">
                                {searchTerm
                                    ? `${t('hierarchical-positions.no-results-message')} "${searchTerm}"`
                                    : positionFilter === "current"
                                        ? t('hierarchical-positions.no-current-positions-message')
                                        : positionFilter === "new"
                                            ? t('hierarchical-positions.no-new-positions-message')
                                            : positionFilter === "carried-forward"
                                                ? t('hierarchical-positions.no-carried-forward-positions-message')
                                                : positionFilter === "modified-carried-forward"
                                                    ? t('hierarchical-positions.no-modified-carried-forward-positions-message')
                                                    : t('hierarchical-positions.no-positions-message')
                                }
                            </p>
                        </div>
                    ) : (
                        filteredLVs.map((lv) => {
                            const isExpanded = expandedLVs.has(lv.id);
                            return (
                                <Card key={lv.id} className="lv-card mb-3">
                                    <div className="lv-card-header" onClick={() => toggleLV(lv.id)}>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="lv-expand-icon">
                                                <FontAwesomeIcon
                                                    icon={isExpanded ? faChevronDown : faChevronRight}
                                                    className="hp-expand-icon"
                                                />
                                            </div>
                                            <FontAwesomeIcon
                                                icon={isExpanded ? faFolderOpen : faFolder}
                                                className="hp-folder-icon"
                                            />
                                            <span className="lv-name">{lv.name}</span>
                                            {lv.isNachtraege && (
                                                <Badge bg="warning" className="hp-badge-xs">
                                                    {t('hierarchical-positions.nachtraege')}
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <Badge bg="secondary" className="hp-badge-xs-muted">
                                                {lv.elements
                                                    ? lv.elements.filter(el => el.isGroup).length
                                                    : 0}{' '}
                                                {t('hierarchical-positions.groups')}
                                            </Badge>
                                            <span className="lv-amount">
                                                {formatCurrencyToLocale(lv.totalPrice || lv.totalPriceGross || 0)}
                                            </span>
                                        </div>
                                    </div>

                                    {isExpanded && lv.elements && (
                                        <Card.Body className="p-0 hp-card-body-tinted">
                                            {lv.elements
                                                .filter(el => el.isGroup)
                                                .map(group => (
                                                    <RecursiveGroupRenderer
                                                        key={group.id}
                                                        element={group}
                                                        level={0}
                                                        expandedGroups={expandedGroups}
                                                        toggleGroup={toggleGroup}
                                                        isReadonly={isReadonly}
                                                    />
                                                ))}
                                            {lv.elements.filter(el => !el.isGroup).length > 0 && (
                                                <div className="p-3">
                                                    <ExpandablePositionsTable
                                                        positions={lv.elements.filter(el => !el.isGroup)}
                                                        onInvoiceQuantityChange={handlePositionQuantityChange}
                                                        isReadonly={isReadonly}
                                                    />
                                                </div>
                                            )}
                                        </Card.Body>
                                    )}
                                </Card>
                            );
                        })
                    )}
                </div>
            </div>
            <div className="positions-legend-strip">
                <PositionTypeLegend />
            </div>
        </div>
    );
};

export default HierarchicalPositions;
